import { clientVersion } from '@trello/config';
import { asString, isBrowserSupported } from '@trello/browser';
import { memberId } from '@trello/session-cookie';

interface LegacyNavigator extends Navigator {
  browserLanguage: string;
}

interface SentryEntry {
  name: string;
  msg: string;
  stack?: string;
  ua: string;
  browser: string;
  isBrowserSupported: boolean;
  language: string;
  clientVersion: { head: string; version: number; patch: number };
  tags: {
    [key: string]: boolean | string;
  };
  idMember?: string;
}

interface ErrorEvent {
  // the error that was encountered
  error: Error;
  // a map of tag/value pairs to send along with the entry
  attributes?: { [key: string]: boolean | string };
}

/**
 * Sends an error entry to Sentry
 *
 * @param errorEvent the errorEvent to send to Sentry
 */
export const sendErrorEvent = async (errorEvent: ErrorEvent) => {
  const versionParts = clientVersion.split('-');
  const head = versionParts.slice(0, -1).join('-');
  const version = parseInt(versionParts.slice(-1)[0], 10);
  const memberProperty = memberId ? { idMember: memberId } : {};

  const entry: SentryEntry = {
    name: errorEvent.error.name,
    msg: errorEvent.error.message,
    stack: errorEvent.error.stack,
    ua: navigator.userAgent,
    browser: asString,
    isBrowserSupported: isBrowserSupported(),
    language:
      navigator.language || (navigator as LegacyNavigator).browserLanguage,
    clientVersion: { head, version, patch: 0 },
    tags: {
      isBrowserSupported: isBrowserSupported(),
      ...errorEvent.attributes,
    },
    // Only append the idMember property if there's a memberId.
    ...memberProperty,
  };

  try {
    await fetch('/err', {
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      method: 'POST',
      body: JSON.stringify({ errors: [entry] }),
    });
  } catch (err) {
    throw new Error(
      `There was a problem while logging an error: ${err.message}`,
    );
  }
};
